<template>
  <b-card
    class="mb-0"
  >
    <div class="mb-2">
      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <h4 class="card-title mb-0 pt-50">
            Available Accounts</h4>
        </b-col>
        <b-col
          cols="12"
          md="8 ml-auto"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-input-group>
              <b-form-input
                v-model="searchQuery"
                placeholder="Search"
                @keyup.enter="updateSearchQuery"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-primary"
                  @click="updateSearchQuery"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="14"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-button
              variant="primary"
              class="ml-2 min-w-120"
              :to="{ name: 'finance-and-accounting-banks-new' }"
            >
              Add Bank
            </b-button>
          </div>
        </b-col>
      </b-row>
    </div>

    <b-row>
      <b-col cols="12">
        <vue-good-table
          :columns="columns"
          :rows="banksResponse.banks"
          :rtl="direction"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
          :pagination-options="{
            enabled: true,
            perPage:pageLength
          }"
        >

          <template
            slot="table-row"
            slot-scope="props"
          >

            <!-- Column: Currency -->
            <span v-if="props.column.field === 'cellRendererCreditCurrency'">
              <span>{{ props.row.credits_amount| currency('$ ', 2, { thousandsSeparator: ',' }) }}</span>
            </span>

            <!-- Column: Currency -->
            <span v-if="props.column.field === 'cellRendererDebitCurrency'">
              <span>{{ props.row.debits_amount| currency('$ ', 2, { thousandsSeparator: ',' }) }}</span>
            </span>

            <!-- Column: Bank Transaction -->
            <span v-if="props.column.field === 'CellRendererBankTransactionsButton'">
              <b-button
                variant="outline-primary"
                type="button"
                class="py-50 px-50"
                @click="bankTransactionRecord(props.row)"
              >
                Bank Transactions
              </b-button>
            </span>

            <!-- Column: Details -->
            <span v-if="props.column.field === 'cellRendererDetailsButton'">
              <b-button
                variant="outline-primary"
                type="button"
                class="py-50 px-50"
                :to="{ name: 'finance-and-accounting-bank-reconcile-history-reconcilation-history', params: { id: props.row.id }, query: {account_number: props.row.account_number, bank_name: props.row.bank_name}}"
              >
                Recon History
              </b-button>
            </span>

            <!-- Column: Sync -->
            <span v-if="props.column.field === 'cellRendererSyncButton'">
              <b-button
                variant="outline-primary"
                type="button"
                class="py-50 px-50"
                :to="{ name: 'finance-and-accounting-bank-reconcile-history-reconcile-an-account', params: { id: props.row.id }, query: {account_number: props.row.account_number, bank_name: props.row.bank_name}}"
              >
                Reconcile
              </b-button>
              <b-button
                variant="outline-primary"
                type="button"
                class="ml-50 py-50 px-50"
                @click="syncRecord(props.row)"
              >
                Sync
              </b-button>
            </span>

            <!-- Column: Action -->
            <span
              v-if="props.column.field === 'action'"
              class="btn-action"
            >
              <span>
                <b-dropdown
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="text-body align-middle mr-25"
                    />
                  </template>
                  <b-dropdown-item :to="{ name: 'finance-and-accounting-banks-edit', params: { id: props.row.id } }">
                    <feather-icon icon="Edit2Icon" />
                    <span class="ml-50">Edit</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="confirmDeleteRecord(props.row)">
                    <feather-icon icon="TrashIcon" />
                    <span class="ml-50">Delete</span>
                  </b-dropdown-item>
                </b-dropdown>
                <b-link
                  id="view"
                  :to="{ name: 'finance-and-accounting-banks-view', params: { id: props.row.id } }"
                >
                  <b-badge
                    pill
                    variant="light-primary"
                  ><feather-icon icon="EyeIcon" />
                  </b-badge>
                </b-link>
              </span>
            </span>

            <!-- Column: Common -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
          >
            <div class="d-flex justify-content-end flex-wrap">
              <div>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="banksResponse.count"
                  first-number
                  last-number
                  align="right"
                  :per-page="1"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BPagination, BInputGroup, BFormInput, BInputGroupAppend, BDropdown, BDropdownItem, BButton, BLink, BBadge,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import Bank from '@/js/bank'
import BankTransactions from '@/js/bankTransactions'

export default {
  name: 'BankReconcileHistory',
  components: {
    BCard,
    BRow,
    BCol,
    VueGoodTable,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
  },
  data() {
    return {
      searchQuery: '',
      pageLength: 10,
      dir: false,
      banksResponse: {
        banks: [],
        count: 1,
      },
      columns: [
        {
          label: '#',
          field: 'index',
          width: '80px',
        },
        {
          label: 'Account Name',
          field: 'bank_name',
          width: '180px',
          sortable: false,
        },
        {
          label: 'Last Recon Date',
          field: 'reconcile_date',
          width: '120px',
          sortable: false,
        },
        {
          label: 'Uncleared Credit',
          field: 'credits',
          width: '110px',
          sortable: false,
        },
        {
          label: 'Uncleared Debit',
          field: 'debits',
          width: '110px',
          sortable: false,
        },
        {
          label: 'Uncleared AMT($) Credit',
          field: 'cellRendererCreditCurrency',
          width: '180px',
          sortable: false,
        },
        {
          label: 'Uncleared AMT($) Debit',
          field: 'cellRendererDebitCurrency',
          width: '180px',
          sortable: false,
        },
        {
          label: 'Bank Transactions',
          field: 'CellRendererBankTransactionsButton',
          width: '180px',
          sortable: false,
        },
        {
          label: 'Details',
          field: 'cellRendererDetailsButton',
          width: '145px',
          sortable: false,
        },
        {
          label: 'Sync',
          field: 'cellRendererSyncButton',
          width: '175px',
          sortable: false,
        },
        {
          label: 'Action',
          field: 'action',
          width: '120px',
          sortable: false,
        },
      ],
      rows: [],
      searchTerm: '',
    }
  },
  computed: {
    currentPage: {
      get() {
        return this.$store.state.query.page
      },
      set(val) {
        this.$store.state.query.page = val
        Bank.getBanks(this)
      },
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
  },
  created() {
    this.searchQuery = this.$store.state.query.query
    this.banksResponse.count = this.$store.state.query.page
    Bank.getBanks(this)
  },
  methods: {
    confirmDeleteRecord(obj) {
      this.$swal({
        title: 'Are you sure?',
        text: `you wan't to delete this ${obj.bank_name}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-primary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          Bank.delete(this, obj.id)
        }
      })
    },
    bankDeleted() {
      Bank.getBanks(this)
      this.$swal({
        icon: 'success',
        title: 'Bank Deleted!',
        text: 'Your Bank has been deleted',
      })
    },
    bankNotDeleted() {
      this.$swal({
        icon: 'warning',
        title: 'Bank Not Deleted!',
        text: 'Your Bank has been not deleted',
      })
    },
    updateSearchQuery() {
      this.$store.state.query.query = this.searchQuery
      this.currentPage = 1
    },
    reconRecord(obj) {
      this.$router.push({ path: `/finance-and-accounting/bank-reconcile-history/reconcilation-history/${obj.id}`, query: { account_number: obj.account_number, bank_name: obj.bank_name } }).catch(() => {})
    },
    bankTransactionRecord(obj) {
      this.$router.push({ path: `/finance-and-accounting/bank-transactions/${obj.id}`, query: { account_number: obj.account_number, bank_name: obj.bank_name } }).catch(() => {})
    },
    setBanks(banksResponse) {
      this.banksResponse = banksResponse
    },
    reconcilRecord(obj) {
      this.$router.push({ path: `/finance-and-accounting/bank-reconcile-history/reconcile-an-account/${obj.id}`, query: { account_number: obj.account_number, bank_name: obj.bank_name } }).catch(() => {})
    },
    syncRecord(obj) {
      this.$vs.loading()
      BankTransactions.getTransactions(this, obj.id)
    },
    setTrnsactions(data) {
      this.$vs.loading.close()
      Bank.getBanks(this)
      this.$vs.notify({
        color: 'success',
        title: 'Transactions Synced',
        text: data.return_message,
      })
    },
    setNotTrnsactions(message) {
      this.$vs.loading.close()
      this.$vs.notify({
        color: 'warning',
        title: 'Transactions not Synced',
        text: message,
      })
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
